import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './ChatContent.css';
import {
  shouldRequestPermission,
  requestNotificationPermission,
  isNotificationGranted,
  sendChatMessageNotification,
} from '../../utils/notifications';

// Icons
const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
  </svg>
);

const LoadMoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" fill="currentColor"/>
  </svg>
);

const AttachIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5c0 1.38 1.12 2.5 2.5 2.5s2.5-1.12 2.5-2.5V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z" fill="currentColor"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
  </svg>
);

const FileIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="currentColor"/>
  </svg>
);

const MESSAGES_PER_PAGE = 15;

// Получить текущее время в ISO формате с часовым поясом
const getLocalISOString = () => {
  const now = new Date();
  const offset = -now.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const pad = (n) => String(Math.abs(n)).padStart(2, '0');
  const hours = pad(Math.floor(offset / 60));
  const minutes = pad(offset % 60);
  
  return now.getFullYear() + '-' +
    pad(now.getMonth() + 1) + '-' +
    pad(now.getDate()) + 'T' +
    pad(now.getHours()) + ':' +
    pad(now.getMinutes()) + ':' +
    pad(now.getSeconds()) +
    sign + hours + ':' + minutes;
};

// Singleton WebSocket manager to prevent multiple connections
const WebSocketManager = {
  instance: null,
  connectionId: 0,
  listeners: new Set(),
  reconnectTimeout: null,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  reconnectDelay: 1000,
  isConnecting: false,
  sessionReplaced: false, // Флаг блокировки переподключения при замене сессии

  connect(userId, onMessage, onStatusChange) {
    // Если сессия была заменена — не подключаемся
    if (this.sessionReplaced) {
      console.log('[Chat] Connection blocked - session was replaced on another device');
      return;
    }

    // Register listener
    this.listeners.add({ onMessage, onStatusChange });

    // If already connected or connecting, just return
    if (this.instance && (this.instance.readyState === WebSocket.OPEN || this.instance.readyState === WebSocket.CONNECTING)) {
      if (this.instance.readyState === WebSocket.OPEN) {
        onStatusChange('connected');
      }
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    this.connectionId++;
    const currentConnectionId = this.connectionId;
    
    const wsUrl = `wss://rugoplaydonate.ru/ws/${userId}`;
    
    onStatusChange('connecting');
    
    try {
      this.instance = new WebSocket(wsUrl);
      
      this.instance.onopen = () => {
        if (this.connectionId !== currentConnectionId) {
          this.instance?.close();
          return;
        }
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        console.log('[Chat] Connected to server');
        this.notifyStatusChange('connected');
      };
      
      this.instance.onmessage = (event) => {
        this.notifyMessage(event);
      };
      
      this.instance.onclose = () => {
        this.isConnecting = false;
        console.log('[Chat] Disconnected from server');
        this.notifyStatusChange('disconnected');
        
        // Не переподключаемся если сессия была заменена
        if (!this.sessionReplaced && this.listeners.size > 0 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect(userId);
        }
      };
      
      this.instance.onerror = (error) => {
        this.isConnecting = false;
        console.error('[Chat] WebSocket error:', error);
        this.notifyStatusChange('error');
      };
    } catch (error) {
      this.isConnecting = false;
      console.error('[Chat] Failed to connect:', error);
      onStatusChange('error');
    }
  },

  scheduleReconnect(userId) {
    // Не переподключаемся если сессия была заменена
    if (this.sessionReplaced) {
      console.log('[Chat] Reconnect blocked - session was replaced');
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    
    this.reconnectTimeout = setTimeout(() => {
      if (this.listeners.size > 0 && !this.sessionReplaced) {
        this.reconnectAttempts++;
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
        console.log(`[Chat] Reconnecting... Attempt ${this.reconnectAttempts}`);
        
        const listener = Array.from(this.listeners)[0];
        if (listener) {
          this.connect(userId, listener.onMessage, listener.onStatusChange);
        }
      }
    }, this.reconnectDelay);
  },

  // Помечаем сессию как замененную — блокирует все переподключения
  markSessionReplaced() {
    this.sessionReplaced = true;
    console.log('[Chat] Session marked as replaced - reconnection disabled');
    
    // Отменяем запланированные переподключения
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Закрываем текущее соединение
    if (this.instance) {
      this.instance.onclose = null; // Убираем обработчик чтобы не было попытки реконнекта
      this.instance.close();
      this.instance = null;
    }
    
    this.listeners.clear();
    this.isConnecting = false;
  },

  disconnect(onMessage, onStatusChange) {
    this.listeners.forEach(listener => {
      if (listener.onMessage === onMessage && listener.onStatusChange === onStatusChange) {
        this.listeners.delete(listener);
      }
    });

    if (this.listeners.size === 0) {
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
      
      if (this.instance) {
        this.instance.onclose = null;
        this.instance.close();
        this.instance = null;
      }
      
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.isConnecting = false;
      // Сбрасываем флаг при обычном отключении (навигация/unmount)
      this.sessionReplaced = false;
    }
  },

  send(data) {
    if (this.instance && this.instance.readyState === WebSocket.OPEN) {
      this.instance.send(data);
      return true;
    }
    return false;
  },

  notifyMessage(event) {
    this.listeners.forEach(listener => {
      try {
        listener.onMessage(event);
      } catch (e) {
        console.error('[Chat] Error in message listener:', e);
      }
    });
  },

  notifyStatusChange(status) {
    this.listeners.forEach(listener => {
      try {
        listener.onStatusChange(status);
      } catch (e) {
        console.error('[Chat] Error in status listener:', e);
      }
    });
  },

  isOpen() {
    return this.instance && this.instance.readyState === WebSocket.OPEN;
  }
};

const ChatContent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allMessages, setAllMessages] = useState([]); // All messages from history
  const [displayedCount, setDisplayedCount] = useState(MESSAGES_PER_PAGE); // How many to show
  const [inputValue, setInputValue] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showSessionReplacedPopup, setShowSessionReplacedPopup] = useState(false);
  const [serverHistoryLoaded, setServerHistoryLoaded] = useState(false); // Флаг загрузки истории с сервера
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const skipScrollRef = useRef(false);
  
  // Use authenticated user's ID
  const userId = user?.id;

  // Get messages to display (last N messages)
  const displayedMessages = allMessages.slice(-displayedCount);
  const hasMoreMessages = allMessages.length > displayedCount;

  // Check and request notification permission automatically
  const checkNotificationPermission = useCallback(async () => {
    if (shouldRequestPermission()) {
      // Сразу запрашиваем разрешение через браузерный диалог
      const permission = await requestNotificationPermission();
      if (permission === 'granted') {
        console.log('[Chat] Push notifications enabled');
      } else {
        console.log('[Chat] Push notifications denied or dismissed');
      }
    } else if (isNotificationGranted()) {
      console.log('[Chat] Push notifications already granted');
    } else {
      console.log('[Chat] Push notifications denied');
    }
  }, []);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    // Skip scrolling if we just loaded more messages
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }
    scrollToBottom();
  }, [allMessages, displayedCount, scrollToBottom]);

  // Save chat history to localStorage
  const saveChatHistory = useCallback((msgs) => {
    if (!userId) return;
    localStorage.setItem(`webchat_history_${userId}`, JSON.stringify(msgs));
  }, [userId]);

  // Load chat history from localStorage
  const loadChatHistory = useCallback(() => {
    if (!userId) return;
    const history = localStorage.getItem(`webchat_history_${userId}`);
    if (history) {
      try {
        const parsed = JSON.parse(history);
        setAllMessages(parsed);
        setDisplayedCount(MESSAGES_PER_PAGE);
      } catch (e) {
        console.error('[Chat] Failed to parse chat history:', e);
      }
    }
  }, [userId]);

  // Clear chat history when receiving messages from server
  const clearChatHistory = useCallback(() => {
    if (!userId) return;
    localStorage.removeItem(`webchat_history_${userId}`);
    setAllMessages([]);
    setDisplayedCount(MESSAGES_PER_PAGE);
  }, [userId]);

  // Load more messages
  const loadMoreMessages = useCallback(() => {
    const scrollContainer = messagesContainerRef.current;
    if (!scrollContainer) return;
    
    // Save current scroll position from top
    const previousScrollHeight = scrollContainer.scrollHeight;
    const previousScrollTop = scrollContainer.scrollTop;
    
    // Mark that we should skip auto-scroll
    skipScrollRef.current = true;
    setIsLoadingMore(true);
    
    setDisplayedCount(prev => {
      const newCount = Math.min(prev + MESSAGES_PER_PAGE, allMessages.length);
      
      // Restore scroll position after DOM updates
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (scrollContainer) {
            const newScrollHeight = scrollContainer.scrollHeight;
            const heightDiff = newScrollHeight - previousScrollHeight;
            scrollContainer.scrollTop = previousScrollTop + heightDiff;
          }
          setIsLoadingMore(false);
        });
      });
      
      return newCount;
    });
  }, [allMessages.length]);

  // Check if message is a duplicate
  const isDuplicateMessage = useCallback((prev, newMsg) => {
    // Check by message_id if available
    if (newMsg.id && prev.some(msg => msg.id === newMsg.id)) {
      return true;
    }
    
    // Check by text + timestamp (within 5 seconds) + type
    const newTime = new Date(newMsg.timestamp).getTime();
    return prev.some(msg => {
      if (msg.text !== newMsg.text || msg.type !== newMsg.type) {
        return false;
      }
      const msgTime = new Date(msg.timestamp).getTime();
      return Math.abs(newTime - msgTime) < 5000; // Within 5 seconds
    });
  }, []);

  // Handle incoming WebSocket message
  const handleMessage = useCallback((event) => {
    try {
      const message = JSON.parse(event.data);
      
      switch (message.type) {
        case 'edit_message':
          // Edit existing message
          setAllMessages(prev => {
            const messageId = message.message_id;
            if (messageId) {
              const updated = prev.map(msg => {
                if (msg.id === messageId) {
                  return {
                    ...msg,
                    text: message.message,
                    keyboard: message.keyboard,
                    media: message.media,
                    media_type: message.media_type,
                    media_name: message.media_name,
                    timestamp: message.timestamp || msg.timestamp
                  };
                }
                return msg;
              });
              saveChatHistory(updated);
              return updated;
            }
            return prev;
          });
          break;
        case 'system':
          setAllMessages(prev => {
            const newMsg = {
              id: message.message_id || (Date.now() + Math.random()),
              type: 'system',
              text: message.message,
              timestamp: message.timestamp || getLocalISOString(),
              keyboard: message.keyboard,
              media: message.media,
              media_type: message.media_type,
              media_name: message.media_name
            };
            
            // Check for duplicate
            if (isDuplicateMessage(prev, newMsg)) {
              return prev;
            }
            
            const updated = [...prev, newMsg];
            saveChatHistory(updated);
            setDisplayedCount(count => count + 1);
            
            // Send push notification for system messages
            sendChatMessageNotification(newMsg);
            
            return updated;
          });
          break;
        case 'admin_message':
          setAllMessages(prev => {
            const newMsg = {
              id: message.message_id || (Date.now() + Math.random()),
              type: 'admin',
              text: message.message,
              timestamp: message.timestamp || getLocalISOString(),
              keyboard: message.keyboard,
              media: message.media,
              media_type: message.media_type,
              media_name: message.media_name
            };
            
            // Check for duplicate
            if (isDuplicateMessage(prev, newMsg)) {
              return prev;
            }
            
            const updated = [...prev, newMsg];
            saveChatHistory(updated);
            setDisplayedCount(count => count + 1);
            
            // Send push notification for admin messages
            sendChatMessageNotification(newMsg);
            
            return updated;
          });
          setIsTyping(false);
          break;
        case 'typing':
          setIsTyping(true);
          setTimeout(() => setIsTyping(false), 3000);
          break;
        case 'message_sent':
          break;
        case 'user_message':
          // Обработка сообщений пользователя из истории
          setAllMessages(prev => {
            const newMsg = {
              id: message.message_id || (Date.now() + Math.random()),
              type: 'user',
              text: message.message,
              timestamp: message.timestamp || getLocalISOString(),
              media: message.media,
              media_type: message.media_type,
              media_name: message.media_name
            };
            
            // Check for duplicate
            if (isDuplicateMessage(prev, newMsg)) {
              return prev;
            }
            
            const updated = [...prev, newMsg];
            saveChatHistory(updated);
            setDisplayedCount(count => count + 1);
            return updated;
          });
          break;
        case 'session_replaced':
          // Сессия была заменена на другом устройстве
          console.log('[Chat] Session replaced on another device');
          setShowSessionReplacedPopup(true);
          // Полностью блокируем переподключение до перезагрузки страницы
          WebSocketManager.markSessionReplaced();
          break;
        default:
          console.log('[Chat] Unknown message type:', message.type);
      }
    } catch (e) {
      console.error('[Chat] Failed to parse message:', e);
    }
  }, [saveChatHistory, isDuplicateMessage]);

  // Handle status change
  const handleStatusChange = useCallback((status) => {
    setConnectionStatus(status);
    setIsConnected(status === 'connected');
  }, []);

  // Refs for stable callback references
  const handleMessageRef = useRef(handleMessage);
  const handleStatusChangeRef = useRef(handleStatusChange);
  
  // Keep refs updated
  useEffect(() => {
    handleMessageRef.current = handleMessage;
    handleStatusChangeRef.current = handleStatusChange;
  }, [handleMessage, handleStatusChange]);

  // Check notification permission on mount
  useEffect(() => {
    checkNotificationPermission();
  }, [checkNotificationPermission]);

  // Don't load chat history from localStorage on mount
  // Server will send full history when connecting to WebSocket
  // This prevents duplicate messages
  useEffect(() => {
    if (userId) {
      // Clear any stale localStorage data to prevent conflicts
      clearChatHistory();
    }
  }, [userId, clearChatHistory]);

  // Initialize WebSocket connection - only depends on userId
  useEffect(() => {
    if (!userId) return;
    
    // Stable wrapper functions that use refs
    const messageHandler = (event) => handleMessageRef.current(event);
    const statusHandler = (status) => handleStatusChangeRef.current(status);
    
    WebSocketManager.connect(userId, messageHandler, statusHandler);
    
    return () => {
      WebSocketManager.disconnect(messageHandler, statusHandler);
    };
  }, [userId]); // Only reconnect when userId changes

  // Handle selected game from AllGamesContent
  useEffect(() => {
    if (!userId || !isConnected) return;
    
    const selectedGame = localStorage.getItem('selected_game_for_chat');
    if (selectedGame) {
      // Remove from localStorage to prevent repeated calls
      localStorage.removeItem('selected_game_for_chat');
      
      // Wait a bit for the WebSocket to be fully ready
      setTimeout(() => {
        // First, call admin (send callback for "call_admin")
        const callAdminCallback = {
          type: 'callback_query',
          callback_data: 'call_admin',
          button_text: 'Купить донат',
          timestamp: getLocalISOString()
        };
        
        // Add user message
        const userMessage = {
          id: Date.now() + Math.random(),
          type: 'user',
          text: 'Купить донат',
          timestamp: getLocalISOString()
        };
        
        setAllMessages(prev => {
          const updated = [...prev, userMessage];
          saveChatHistory(updated);
          return updated;
        });
        setDisplayedCount(count => count + 1);
        
        // Send callback to server
        WebSocketManager.send(JSON.stringify(callAdminCallback));
        
        // Then, send a message with the game name
        setTimeout(() => {
          const gameMessage = {
            id: Date.now() + Math.random(),
            type: 'user',
            text: `Игра: ${selectedGame}`,
            timestamp: getLocalISOString()
          };
          
          setAllMessages(prev => {
            const updated = [...prev, gameMessage];
            saveChatHistory(updated);
            return updated;
          });
          setDisplayedCount(count => count + 1);
          
          WebSocketManager.send(JSON.stringify({
            type: 'user_message',
            message: `Игра: ${selectedGame}`,
            timestamp: getLocalISOString()
          }));
        }, 500);
      }, 1000);
    }
  }, [userId, isConnected, saveChatHistory]);

  // Handle file selection
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Limit to 5 files at a time
    const newFiles = files.slice(0, 5 - selectedFiles.length);
    
    // Create preview URLs for images/videos
    const filesWithPreview = newFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      name: file.name,
      type: file.type,
      size: file.size,
      preview: file.type.startsWith('image/') || file.type.startsWith('video/') 
        ? URL.createObjectURL(file) 
        : null
    }));
    
    setSelectedFiles(prev => [...prev, ...filesWithPreview]);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedFiles.length]);

  // Handle paste from clipboard (for images)
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if it's an image
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          // Generate a name for the pasted image
          const extension = item.type.split('/')[1] || 'png';
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const namedFile = new File([file], `pasted-image-${timestamp}.${extension}`, {
            type: file.type
          });
          imageFiles.push(namedFile);
        }
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault(); // Prevent default paste behavior for images
      
      // Limit to 5 files at a time
      const availableSlots = 5 - selectedFiles.length;
      const newFiles = imageFiles.slice(0, availableSlots);
      
      if (newFiles.length === 0) {
        console.log('[Chat] Maximum files limit reached');
        return;
      }
      
      // Create preview URLs for images
      const filesWithPreview = newFiles.map(file => ({
        file,
        id: Date.now() + Math.random(),
        name: file.name,
        type: file.type,
        size: file.size,
        preview: URL.createObjectURL(file)
      }));
      
      setSelectedFiles(prev => [...prev, ...filesWithPreview]);
      console.log(`[Chat] Pasted ${newFiles.length} image(s) from clipboard`);
    }
  }, [selectedFiles.length]);

  // Remove selected file
  const removeFile = useCallback((fileId) => {
    setSelectedFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  }, []);

  // Upload a single file
  const uploadFile = useCallback(async (fileData) => {
    const formData = new FormData();
    formData.append('file', fileData.file);
    
    try {
      const response = await fetch(`https://rugoplaydonate.ru/upload_media/${userId}`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('[Chat] File upload error:', error);
      throw error;
    }
  }, [userId]);

  // Send message (with optional files)
  const sendMessage = useCallback(async () => {
    const text = inputValue.trim();
    const hasFiles = selectedFiles.length > 0;
    
    if (!text && !hasFiles) return;
    if (!isConnected) return;
    
    // If we have files, upload them first
    if (hasFiles) {
      setIsUploading(true);
      
      for (const fileData of selectedFiles) {
        try {
          const result = await uploadFile(fileData);
          
          // Add message for each uploaded file
          const newMessage = {
            id: Date.now() + Math.random(),
            type: 'user',
            text: `📎 ${fileData.name}`,
            timestamp: getLocalISOString(),
            media: result.url,
            media_type: fileData.type,
            media_name: fileData.name
          };
          
          setAllMessages(prev => {
            const updated = [...prev, newMessage];
            saveChatHistory(updated);
            return updated;
          });
          setDisplayedCount(count => count + 1);
          
          // Revoke preview URL
          if (fileData.preview) {
            URL.revokeObjectURL(fileData.preview);
          }
        } catch (error) {
          // Show error message
          const errorMessage = {
            id: Date.now() + Math.random(),
            type: 'system',
            text: `❌ Ошибка загрузки файла "${fileData.name}": ${error.message}`,
            timestamp: getLocalISOString()
          };
          setAllMessages(prev => [...prev, errorMessage]);
          setDisplayedCount(count => count + 1);
        }
      }
      
      setSelectedFiles([]);
      setIsUploading(false);
    }
    
    // Send text message if any
    if (text) {
      const newMessage = {
        id: Date.now() + Math.random(),
        type: 'user',
        text: text,
        timestamp: getLocalISOString()
      };

      setAllMessages(prev => {
        const updated = [...prev, newMessage];
        saveChatHistory(updated);
        return updated;
      });
      setDisplayedCount(count => count + 1);

      WebSocketManager.send(JSON.stringify({
        type: 'user_message',
        message: text,
        timestamp: getLocalISOString()
      }));

      setInputValue('');
    }
  }, [inputValue, isConnected, saveChatHistory, selectedFiles, uploadFile]);

  // Send callback query (for inline keyboard buttons)
  // При нажатии на кнопку: 1) добавляем сообщение пользователя, 2) отправляем callback на сервер
  const sendCallback = useCallback((callbackData, messageId, buttonText) => {
    if (!isConnected) return;
    
    // Добавляем сообщение от пользователя с текстом кнопки
    const userMessage = {
      id: Date.now() + Math.random(),
      type: 'user',
      text: buttonText,
      timestamp: getLocalISOString()
    };
    
    setAllMessages(prev => {
      const updated = [...prev, userMessage];
      saveChatHistory(updated);
      return updated;
    });
    setDisplayedCount(count => count + 1);
    
    // Отправляем callback query на сервер
    WebSocketManager.send(JSON.stringify({
      type: 'callback_query',
      callback_data: callbackData,
      message_id: messageId,
      button_text: buttonText,
      timestamp: getLocalISOString()
    }));
  }, [isConnected, saveChatHistory]);

  // Open file picker
  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Format time - конвертирует московское время сервера в локальное время пользователя
  const formatTime = (timestamp) => {
    // Время с сервера приходит в формате ISO с часовым поясом (например: 2025-12-24T15:30:00+03:00)
    // new Date() автоматически конвертирует его в локальное время браузера
    const date = new Date(timestamp);
    
    // Проверяем валидность даты
    if (isNaN(date.getTime())) {
      return '--:--';
    }
    
    // toLocaleTimeString автоматически выводит время в часовом поясе пользователя
    return date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Преобразует текст, делая URL кликабельными и поддерживая HTML-теги <a>, <b>, <s>, <i>, <u>
  const renderTextWithLinks = (text) => {
    if (!text) return null;
    
    let keyCounter = 0;
    
    // Рекурсивная функция для парсинга текста с HTML-тегами
    const parseFormattedText = (str) => {
      const elements = [];
      
      // Регулярное выражение для поиска HTML-тегов и URL
      const tagRegex = /<(a)\s+href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/\1>|<(b|strong|s|strike|i|em|u)>([\s\S]*?)<\/\4>|(https?:\/\/[^\s<]+)|((?:t\.me|vk\.com)\/[^\s<]+)/gi;
      
      let lastIndex = 0;
      let match;
      
      while ((match = tagRegex.exec(str)) !== null) {
        // Добавляем текст до совпадения
        if (match.index > lastIndex) {
          elements.push(
            <React.Fragment key={keyCounter++}>
              {str.slice(lastIndex, match.index)}
            </React.Fragment>
          );
        }
        
        if (match[1] === 'a' && match[2] && match[3]) {
          // HTML ссылка <a href="url">content</a>
          elements.push(
            <a
              key={keyCounter++}
              href={match[2]}
              target="_blank"
              rel="noopener noreferrer"
              className="chat-message__link"
            >
              {parseFormattedText(match[3])}
            </a>
          );
        } else if (match[4] && match[5] !== undefined) {
          // Теги форматирования: <b>, <strong>, <s>, <strike>, <i>, <em>, <u>
          const tag = match[4].toLowerCase();
          const content = parseFormattedText(match[5]);
          
          if (tag === 'b' || tag === 'strong') {
            elements.push(
              <strong key={keyCounter++} className="chat-message__bold">
                {content}
              </strong>
            );
          } else if (tag === 's' || tag === 'strike') {
            elements.push(
              <s key={keyCounter++} className="chat-message__strikethrough">
                {content}
              </s>
            );
          } else if (tag === 'i' || tag === 'em') {
            elements.push(
              <em key={keyCounter++} className="chat-message__italic">
                {content}
              </em>
            );
          } else if (tag === 'u') {
            elements.push(
              <u key={keyCounter++} className="chat-message__underline">
                {content}
              </u>
            );
          }
        } else if (match[6]) {
          // Обычный URL с http/https
          elements.push(
            <a
              key={keyCounter++}
              href={match[6]}
              target="_blank"
              rel="noopener noreferrer"
              className="chat-message__link"
            >
              {match[6]}
            </a>
          );
        } else if (match[7]) {
          // URL без протокола (t.me или vk.com)
          elements.push(
            <a
              key={keyCounter++}
              href={`https://${match[7]}`}
              target="_blank"
              rel="noopener noreferrer"
              className="chat-message__link"
            >
              {match[7]}
            </a>
          );
        }
        
        lastIndex = match.index + match[0].length;
      }
      
      // Добавляем оставшийся текст
      if (lastIndex < str.length) {
        elements.push(
          <React.Fragment key={keyCounter++}>
            {str.slice(lastIndex)}
          </React.Fragment>
        );
      }
      
      return elements.length > 0 ? elements : str;
    };
    
    return parseFormattedText(text);
  };

  // Render inline keyboard
  const renderKeyboard = (keyboard, messageId) => {
    if (!keyboard || !keyboard.inline_keyboard) return null;

    return (
      <div className="chat-keyboard">
        {keyboard.inline_keyboard.map((row, rowIndex) => (
          <div key={rowIndex} className="chat-keyboard__row">
            {row.map((button, btnIndex) => {
              if (button.url) {
                return (
                  <a
                    key={btnIndex}
                    href={button.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`chat-keyboard__btn chat-keyboard__btn--${button.style || 'secondary'}`}
                  >
                    {button.text}
                  </a>
                );
              }
              return (
                <button
                  key={btnIndex}
                  className={`chat-keyboard__btn chat-keyboard__btn--${button.style || 'secondary'}`}
                  onClick={() => sendCallback(button.callback_data, messageId, button.text)}
                >
                  {button.text}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // Render media
  const renderMedia = (message) => {
    if (!message.media) return null;

    if (message.media_type?.startsWith('video')) {
      return (
        <div className="chat-message__media">
          <video controls className="chat-message__video">
            <source src={message.media} type={message.media_type} />
          </video>
        </div>
      );
    }

    if (message.media_type?.startsWith('image')) {
      return (
        <div className="chat-message__media">
          <img src={message.media} alt={message.media_name || 'Изображение'} className="chat-message__image" />
        </div>
      );
    }

    return null;
  };

  // Get connection status text
  const getConnectionStatusText = () => {
    if (!userId) return 'Загрузка...';
    switch (connectionStatus) {
      case 'connecting':
        return 'Подключение...';
      case 'connected':
        return 'Подключено';
      case 'disconnected':
        return 'Отключено. Переподключение...';
      case 'error':
        return 'Ошибка подключения';
      default:
        return '';
    }
  };

  return (
    <div className="like-profile-sidebar-chat">
      <div className="chat-content">
        {/* Header */}
        <div className="chat-content__header">
          <div className="chat-content__header-info">
            <h2 className="chat-content__title">Диалог с администратором</h2>
            <div className={`chat-content__status chat-content__status--${userId ? connectionStatus : 'connecting'}`}>
              <span className="chat-content__status-dot"></span>
              <span>{getConnectionStatusText()}</span>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="chat-content__messages" ref={messagesContainerRef}>
          {/* Load more button */}
          {hasMoreMessages && (
            <div className="chat-content__load-more">
              <button 
                className="chat-content__load-more-btn"
                onClick={loadMoreMessages}
                disabled={isLoadingMore}
              >
                <LoadMoreIcon />
                <span>{isLoadingMore ? 'Загрузка...' : 'Загрузить ещё'}</span>
              </button>
            </div>
          )}

          {displayedMessages.length === 0 && !hasMoreMessages && (
            <div className="chat-content__empty">
              <p>Начните диалог с администратором</p>
              <p className="chat-content__empty-hint">Напишите ваш вопрос, и мы ответим вам в ближайшее время</p>
            </div>
          )}
          
          {displayedMessages.map((message) => (
            <div key={message.id} className={`chat-message chat-message--${message.type}`}>
              <div className="chat-message__content">
                <div className="chat-message__text">{renderTextWithLinks(message.text)}</div>
                {renderMedia(message)}
                <div className="chat-message__time">{formatTime(message.timestamp)}</div>
              </div>
              {renderKeyboard(message.keyboard, message.id)}
            </div>
          ))}
          
          {isTyping && (
            <div className="chat-message chat-message--admin chat-message--typing">
              <div className="chat-message__content">
                <div className="chat-typing">
                  <span className="chat-typing__dot"></span>
                  <span className="chat-typing__dot"></span>
                  <span className="chat-typing__dot"></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* File Preview */}
        {selectedFiles.length > 0 && (
          <div className="chat-content__file-preview">
            {selectedFiles.map((fileData) => (
              <div key={fileData.id} className="chat-content__file-item">
                {fileData.preview ? (
                  fileData.type.startsWith('video/') ? (
                    <video src={fileData.preview} className="chat-content__file-thumbnail" />
                  ) : (
                    <img src={fileData.preview} alt={fileData.name} className="chat-content__file-thumbnail" />
                  )
                ) : (
                  <div className="chat-content__file-icon">
                    <FileIcon />
                  </div>
                )}
                <span className="chat-content__file-name" title={fileData.name}>
                  {fileData.name.length > 15 ? fileData.name.substring(0, 12) + '...' : fileData.name}
                </span>
                <button 
                  className="chat-content__file-remove"
                  onClick={() => removeFile(fileData.id)}
                  title="Удалить файл"
                >
                  <CloseIcon />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="chat-content__input-container">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx,.txt,.zip,.rar"
            style={{ display: 'none' }}
          />
          
          {/* Attach button */}
          <button
            className="chat-content__attach-btn"
            onClick={openFilePicker}
            disabled={!isConnected || isUploading || selectedFiles.length >= 5}
            title="Прикрепить файл"
          >
            <AttachIcon />
          </button>

          <textarea
            className="chat-content__input"
            placeholder="Напишите сообщение..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            onPaste={handlePaste}
            rows="1"
            disabled={!isConnected || isUploading}
          />
          <button
            className="chat-content__send-btn"
            onClick={sendMessage}
            disabled={!isConnected || isUploading || (!inputValue.trim() && selectedFiles.length === 0)}
            title="Отправить сообщение"
          >
            {isUploading ? (
              <div className="chat-content__send-loader" />
            ) : (
              <SendIcon />
            )}
          </button>
        </div>
      </div>

      {/* Session Replaced Popup */}
      {showSessionReplacedPopup && (
        <div className="chat-content__session-popup-overlay">
          <div className="chat-content__session-popup">
            <div className="chat-content__session-popup-icon">📱</div>
            <h3 className="chat-content__session-popup-title">Чат открыт на другом устройстве</h3>
            <p className="chat-content__session-popup-text">
              Вы открыли чат на другом устройстве. Для продолжения работы используйте то устройство.
            </p>
            <button 
              className="chat-content__session-popup-btn"
              onClick={() => {
                setShowSessionReplacedPopup(false);
                navigate('/orders');
              }}
            >
              Перейти в историю заказов
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatContent;
